import type {
  Hours,
  ReservationStatus,
  ReservationView,
  StaffReservationRequest,
  MenuCategory,
  MenuDraft,
  MenuItem,
  ModifierGroup,
  NewMenuItem,
  NewModifierGroup,
  RestaurantProfile,
  RestaurantSettings
} from "@glowin/schemas";
import { getConfig } from "./config.js";
import type { Order, OrderState } from "./orders-model.js";
import type { ApplicationView, UploadedFileView } from "./onboarding-model.js";
import { currentToken, recoverSession, renewToken } from "./auth.js";

/** Error carrying the server's structured validation problems for display. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly problems: string[] = []
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Which backing service a call goes to. They are separately deployed APIs. */
type Service = "profile" | "order" | "billing" | "onboarding" | "reservation";

function baseUrl(service: Service): string {
  const cfg = getConfig();
  if (service === "order") return cfg.orderApiUrl;
  if (service === "billing") return cfg.billingApiUrl;
  if (service === "reservation") {
    if (!cfg.reservationApiUrl) {
      throw new ApiError(503, "Bookings aren't available in this environment yet.");
    }
    return cfg.reservationApiUrl;
  }
  if (service === "onboarding") {
    // Optional in config (see AppConfig). A 503 here reads the same as the server's own
    // "not configured" answer, so the UI has one case to handle rather than two.
    if (!cfg.onboardingApiUrl) {
      throw new ApiError(503, "Onboarding isn't configured for this environment yet.");
    }
    return cfg.onboardingApiUrl;
  }
  return cfg.apiUrl;
}

/** 401 recovery: renew once, then fall back to a full sign-in. */
const SESSION_EXPIRED = "Your session expired. Signing you in again…";

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  service: Service = "profile"
): Promise<T> {
  const send = (token: string): Promise<Response> =>
    fetch(`${baseUrl(service)}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { "content-type": "application/json" })
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });

  const token = await currentToken();
  if (!token) {
    // No usable session at all: go straight back through sign-in.
    await recoverSession();
    throw new ApiError(401, SESSION_EXPIRED);
  }

  let res = await send(token);

  // The API rejected the token even though it looked valid to us (clock skew, a key
  // rotation, or a revoked session). Renew once and retry before disturbing the user.
  if (res.status === 401) {
    const renewed = await renewToken();
    if (renewed) res = await send(renewed);
    if (res.status === 401) {
      await recoverSession();
      throw new ApiError(401, SESSION_EXPIRED);
    }
  }

  const text = await res.text();
  const payload: unknown = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const p = payload as { error?: string; message?: string; problems?: string[] };
    throw new ApiError(
      res.status,
      p.message ?? p.error ?? `request failed (${res.status})`,
      p.problems ?? []
    );
  }
  return payload as T;
}

/** A restaurant the signed-in user may administer. */
export interface RestaurantRef {
  restaurantId: string;
  /** Absent until the restaurant has a profile row. */
  displayName?: string;
}

export type CallOutcome = "ORDER_PLACED" | "RESERVED" | "ABANDONED" | "TRANSFERRED" | "FAILED";

export interface CallSummary {
  callSid: string;
  startedAtIso: string;
  durationSeconds: number;
  /** Billed time: the duration rounded up to whole 15-second increments (60 for calls billed before). */
  billableSeconds: number;
  /** Cents. */
  billedMinor: number;
  outcome: CallOutcome;
  orderId?: string;
  /** A message the caller left, when the agent couldn't help. Needs a human. */
  message?: string;
  /** The order this call produced; its value comes from the order itself. */
  fromNumber?: string;
}

export interface BillingSummaryView {
  calls: number;
  billableSeconds: number;
  billedMinor: number;
  ordersPlaced: number;
  conversionRate: number;
  costPerOrderMinor?: number;
  /** The billing rule, from the API so the page never states a price the server does not charge. */
  incrementSeconds: number;
  ratePerIncrementMinor: number;
}

export interface TranscriptView {
  callSid: string;
  startedAtIso: string;
  truncated: boolean;
  entries: { atMs: number; role: "agent" | "caller" | "tool" | "system"; text: string }[];
}

export interface FieldChange {
  field: string;
  from: string;
  to: string;
}
export interface ItemSummary {
  itemId: string;
  name: string;
  price: string;
}
export interface PublishPreview {
  liveVersion: number;
  /** Reasons publishing would be rejected; empty means it will succeed. */
  problems: string[];
  diff: {
    isFirstPublish: boolean;
    hasChanges: boolean;
    items: {
      added: ItemSummary[];
      removed: ItemSummary[];
      changed: { itemId: string; name: string; changes: FieldChange[] }[];
    };
    noLongerOffered: ItemSummary[];
    offeredAgain: ItemSummary[];
    categories: {
      added: string[];
      removed: string[];
      renamed: FieldChange[];
      reordered: boolean;
    };
    groups: {
      added: string[];
      removed: string[];
      changed: { groupId: string; name: string; changes: FieldChange[]; affects: string[] }[];
    };
  };
}

export interface VersionSummary {
  version: number;
  publishedBy: string;
  publishedAtIso: string;
  note?: string;
}

const r = (restaurantId: string) => `/restaurants/${encodeURIComponent(restaurantId)}`;

export const api = {
  /** Restaurants this signed-in user may administer (from the membership table). */
  listRestaurants: () =>
    request<{ restaurants: RestaurantRef[]; isGlowinAdmin: boolean }>("GET", "/restaurants"),

  getProfile: (id: string) => request<RestaurantProfile>("GET", r(id)),
  putProfile: (id: string, profile: RestaurantProfile) =>
    request<RestaurantProfile>("PUT", r(id), profile),

  // --- Menu ---
  getDraft: (id: string) => request<MenuDraft>("GET", `${r(id)}/menu/draft`),

  putItem: (id: string, item: MenuItem) =>
    request<MenuItem>("PUT", `${r(id)}/menu/draft/items/${encodeURIComponent(item.itemId)}`, item),

  deleteItem: (id: string, itemId: string) =>
    request<{ deleted: string }>(
      "DELETE",
      `${r(id)}/menu/draft/items/${encodeURIComponent(itemId)}`
    ),

  setAvailability: (id: string, itemId: string, available: boolean) =>
    request<{ itemId: string; available: boolean }>(
      "PATCH",
      `${r(id)}/menu/draft/items/${encodeURIComponent(itemId)}/availability`,
      { available }
    ),

  /** Create a dish. The server mints the itemId from the name. */
  createItem: (id: string, item: NewMenuItem) =>
    request<MenuItem>("POST", `${r(id)}/menu/draft/items`, item),

  // --- Categories ---
  createCategory: (id: string, name: MenuCategory["name"]) =>
    request<MenuCategory>("POST", `${r(id)}/menu/draft/categories`, { name }),

  updateCategory: (id: string, category: MenuCategory) =>
    request<MenuCategory>(
      "PUT",
      `${r(id)}/menu/draft/categories/${encodeURIComponent(category.id)}`,
      category
    ),

  deleteCategory: (id: string, categoryId: string) =>
    request<{ deleted: string }>(
      "DELETE",
      `${r(id)}/menu/draft/categories/${encodeURIComponent(categoryId)}`
    ),

  /** Must list every category exactly once — the server rejects a partial order. */
  reorderCategories: (id: string, order: string[]) =>
    request<{ categories: MenuCategory[] }>("POST", `${r(id)}/menu/draft/categories/order`, {
      order
    }),

  // --- Option groups (shared across dishes) ---
  createGroup: (id: string, group: NewModifierGroup) =>
    request<ModifierGroup>("POST", `${r(id)}/modifier-groups`, group),

  updateGroup: (id: string, group: ModifierGroup) =>
    request<ModifierGroup>(
      "PUT",
      `${r(id)}/modifier-groups/${encodeURIComponent(group.id)}`,
      group
    ),

  deleteGroup: (id: string, groupId: string) =>
    request<{ deleted: string }>(
      "DELETE",
      `${r(id)}/modifier-groups/${encodeURIComponent(groupId)}`
    ),

  /** What publishing would change, so it can be confirmed before callers see it. */
  publishPreview: (id: string) =>
    request<PublishPreview>("GET", `${r(id)}/menu/publish/preview`),

  publish: (id: string, note?: string) =>
    request<{ version: number; liveItems: number }>("POST", `${r(id)}/menu/publish`, { note }),

  listVersions: (id: string) =>
    request<{ versions: VersionSummary[]; liveVersion: number }>("GET", `${r(id)}/menu/versions`),

  // --- Hours & settings ---
  getHours: (id: string) => request<Hours>("GET", `${r(id)}/hours`),
  putHours: (id: string, hours: Hours) => request<Hours>("PUT", `${r(id)}/hours`, hours),
  /** Pause orders until `untilIso`. The agent stops taking orders as soon as this lands. */
  pauseOrders: (id: string, body: { untilIso: string; reason?: string }) =>
    request<Hours>("POST", `${r(id)}/hours/closure`, body),
  /** Resume immediately, before the pause would have expired. */
  resumeOrders: (id: string) => request<Hours>("DELETE", `${r(id)}/hours/closure`),

  getSettings: (id: string) => request<RestaurantSettings>("GET", `${r(id)}/settings`),
  putSettings: (id: string, settings: RestaurantSettings) =>
    request<RestaurantSettings>("PUT", `${r(id)}/settings`, settings),

  // --- Table bookings (reservation service) ---

  /** One local date's bookings at the restaurant, in time order, cancelled ones included. */
  listReservations: (id: string, date: string) =>
    request<{ date: string; reservations: ReservationView[]; bookings: number; guests: number }>(
      "GET",
      `${r(id)}/reservations?date=${encodeURIComponent(date)}`,
      undefined,
      "reservation"
    ),
  createReservation: (id: string, body: StaffReservationRequest) =>
    request<ReservationView>("POST", `${r(id)}/reservations`, body, "reservation"),
  setReservationStatus: (id: string, reservationId: string, status: ReservationStatus) =>
    request<ReservationView>(
      "POST",
      `${r(id)}/reservations/${encodeURIComponent(reservationId)}/status`,
      { status },
      "reservation"
    ),

  // --- Orders (order service, not profile) ---

  /**
   * The restaurant's queue, BOUNDED. `since` is a key-range condition server-side, so orders
   * outside the window are never read — this endpoint used to return every order ever placed.
   */
  listOrders: (
    id: string,
    opts: { since?: string; until?: string; limit?: number; cursor?: string } = {}
  ) => {
    const q = new URLSearchParams();
    if (opts.since) q.set("since", opts.since);
    if (opts.until) q.set("until", opts.until);
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.cursor) q.set("cursor", opts.cursor);
    const qs = q.toString();
    return request<{ orders: Order[]; nextCursor?: string }>(
      "GET",
      `${r(id)}/orders${qs ? `?${qs}` : ""}`,
      undefined,
      "order"
    );
  },

  // --- Calls, cost and transcripts (billing service) ---

  /** Calls in a time range, newest first. */
  listCalls: (id: string, range: { since: string; until: string }) =>
    request<{ since: string; until: string; calls: CallSummary[] }>(
      "GET",
      `${r(id)}/calls?${new URLSearchParams(range).toString()}`,
      undefined,
      "billing"
    ),

  getBilling: (id: string, range: { since: string; until: string }) =>
    request<{ since: string; until: string; summary: BillingSummaryView }>(
      "GET",
      `${r(id)}/billing?${new URLSearchParams(range).toString()}`,
      undefined,
      "billing"
    ),

  /** Agent transcript for one call. 404s once it has expired. */
  getTranscript: (id: string, callSid: string) =>
    request<TranscriptView>(
      "GET",
      `${r(id)}/calls/${encodeURIComponent(callSid)}/transcript`,
      undefined,
      "billing"
    ),

  /**
   * A short-lived link to the call's audio. 404s when the call was not recorded (recording is off unless the
   * restaurant turns it on) or the recording is past its 7 days.
   */
  getRecording: (id: string, callSid: string) =>
    request<{ url: string; expiresAtIso: string }>(
      "GET",
      `${r(id)}/calls/${encodeURIComponent(callSid)}/recording`,
      undefined,
      "billing"
    ),

  updateOrderStatus: (id: string, orderId: string, status: OrderState) =>
    request<Order>(
      "POST",
      `${r(id)}/orders/${encodeURIComponent(orderId)}/status`,
      { status },
      "order"
    ),

  // --- Onboarding ---
  //
  // Every call is scoped to the signed-in user by the SERVER, from the token. There is no
  // applicationId in any of these paths, which is why none of them can be pointed at another
  // applicant's application.

  /** Start a new application, or resume the one already in progress. Idempotent. */
  startOrResumeApplication: () =>
    request<ApplicationView>("POST", "/me/application", undefined, "onboarding"),


  submitApplication: () =>
    request<ApplicationView>("POST", "/me/application/submit", undefined, "onboarding"),

  listApplicationUploads: () =>
    request<{ files: UploadedFileView[] }>(
      "GET",
      "/me/application/uploads",
      undefined,
      "onboarding"
    ),

  deleteApplicationUpload: (fileId: string) =>
    request<{ files: UploadedFileView[] }>(
      "DELETE",
      `/me/application/uploads/${encodeURIComponent(fileId)}`,
      undefined,
      "onboarding"
    ),

  /**
   * Attach a menu file.
   *
   * Two steps, and the second does NOT go through our API: ask for a presigned POST (sending only
   * metadata), then post the bytes straight to S3. API Gateway caps payloads at 6MB and base64
   * would inflate a 15MB menu well past it, so routing the file through the API is not an option.
   */
  uploadMenuFile: async (file: File): Promise<void> => {
    const presigned = await request<PresignedUploadView>(
      "POST",
      "/me/application/uploads",
      { filename: file.name, contentType: file.type, sizeBytes: file.size },
      "onboarding"
    );

    const form = new FormData();
    // Policy fields must precede the file part; S3 rejects the post otherwise.
    for (const [key, value] of Object.entries(presigned.fields)) form.append(key, value);
    form.append("file", file);

    // No authorization header: the signature IS the credential, and a bearer token would make S3
    // reject the request.
    const res = await fetch(presigned.url, { method: "POST", body: form });
    if (!res.ok) {
      // S3 answers with XML; surfacing its body would show the applicant a policy dump.
      throw new ApiError(res.status, "That file couldn't be uploaded. Please try again.");
    }
  }
};

interface PresignedUploadView {
  url: string;
  fields: Record<string, string>;
  key: string;
  fileId: string;
}

/** The caller's application, or undefined when they have never started one. */
export async function getMyApplication(): Promise<ApplicationView | undefined> {
  try {
    return await request<ApplicationView>("GET", "/me/application", undefined, "onboarding");
  } catch (err) {
    // 404 is the ordinary "nothing started yet" answer, not a failure worth surfacing.
    if (err instanceof ApiError && err.status === 404) return undefined;
    throw err;
  }
}
